/**
 * @fileoverview Banner global exibido quando o super-admin está em sessão de
 * impersonação — seja acessando como outro usuário (mode=user) ou em outra
 * Empresa (mode=company). Fica fixo no topo, sobrevive a troca de rota e
 * persiste após F5 porque lê o claim do JWT atual.
 *
 * No mode=company, mostra Empresa + Conta ativas e dá setas pra navegar
 * entre Contas da mesma Empresa (groupName) sem logout.
 */

import React, { useContext, useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  UserRoundCheck,
  Building2,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    bar: {
      position: "sticky",
      top: 0,
      zIndex: 1300,
      width: "100%",
      padding: "10px 20px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      background: dark
        ? "linear-gradient(90deg, #1E2A55 0%, #1A234A 100%)"
        : "linear-gradient(90deg, #3D5CFD 0%, #4F6FFF 100%)",
      borderBottom: `1px solid ${dark ? "rgba(91,124,255,0.45)" : "rgba(15,23,42,0.18)"}`,
      color: "#FFFFFF",
      fontSize: 13,
      fontWeight: 500,
      boxShadow: dark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(61,92,253,0.25)",
      [theme.breakpoints.down("sm")]: {
        padding: "10px 14px",
        fontSize: 12,
        flexWrap: "wrap",
      },
    },
    iconChip: {
      width: 28,
      height: 28,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: "rgba(255,255,255,0.18)",
      color: "#FFFFFF",
      border: "1px solid rgba(255,255,255,0.28)",
    },
    message: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      color: "#FFFFFF",
    },
    line: {
      fontSize: 12,
      opacity: 0.85,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    strong: { fontWeight: 700, color: "#FFFFFF", opacity: 1 },
    // Bloco de navegação entre Contas — setas + nome clicável (dropdown).
    contaNav: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      flexShrink: 0,
      background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.22)",
      borderRadius: 10,
      padding: "3px 4px",
    },
    navBtn: {
      width: 26,
      height: 26,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      background: "transparent",
      borderRadius: 7,
      color: "#FFFFFF",
      cursor: "pointer",
      transition: "background .12s",
      "&:hover:not(:disabled)": { background: "rgba(255,255,255,0.18)" },
      "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
    },
    navName: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "0 10px",
      height: 26,
      maxWidth: 220,
      border: "none",
      background: "transparent",
      color: "#FFFFFF",
      cursor: "pointer",
      borderRadius: 7,
      fontFamily: "inherit",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.01em",
      transition: "background .12s",
      "&:hover": { background: "rgba(255,255,255,0.18)" },
    },
    navNameText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    counter: {
      fontSize: 11,
      fontWeight: 600,
      opacity: 0.75,
      paddingLeft: 4,
      paddingRight: 4,
      fontVariantNumeric: "tabular-nums",
    },
    // Dropdown de seleção direta
    dropdownWrap: { position: "relative" },
    dropdownMenu: {
      position: "absolute",
      top: "calc(100% + 6px)",
      right: 0,
      minWidth: 240,
      maxHeight: 320,
      overflowY: "auto",
      background: dark ? "#1c1f22" : "#FFFFFF",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)"}`,
      borderRadius: 12,
      boxShadow: "0 20px 40px rgba(0,0,0,0.35)",
      padding: 6,
      zIndex: 1500,
    },
    dropdownItem: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 8,
      border: "none",
      background: "transparent",
      color: dark ? "#FAFAFA" : "#09090B",
      fontFamily: "inherit",
      fontSize: 13,
      cursor: "pointer",
      textAlign: "left",
      transition: "background .12s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)" },
    },
    dropdownItemActive: {
      background: dark ? "rgba(91,124,255,0.16)" : "rgba(61,92,253,0.10)",
      color: dark ? "#A5B4FC" : "#3D5CFD",
      fontWeight: 600,
    },
    returnBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "7px 14px",
      borderRadius: 10,
      border: "1px solid rgba(255,255,255,0.45)",
      background: "rgba(255,255,255,0.12)",
      color: "#FFFFFF",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background .15s, border-color .15s",
      flexShrink: 0,
      "&:hover": {
        background: "rgba(255,255,255,0.22)",
        borderColor: "#FFFFFF",
      },
      "&:disabled": { opacity: 0.7, cursor: "wait" },
    },
  };
});

const ImpersonationBanner = () => {
  const classes = useStyles();
  const {
    impersonation,
    user,
    returnFromImpersonation,
    listSiblingContas,
    superSwitchCompany,
  } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const [contas, setContas] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [switching, setSwitching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isUserMode = impersonation?.mode === "user";
  const isCompanyMode = impersonation?.mode === "company";

  // Fecha o dropdown ao clicar fora.
  useEffect(() => {
    if (!dropdownOpen) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [dropdownOpen]);

  // Carrega Contas irmãs (mesma Empresa) quando entra em mode=company.
  // Só faz sentido pra super, e o endpoint é gated por isAuth + super check.
  useEffect(() => {
    if (!isCompanyMode) {
      setContas([]);
      setCurrentIndex(-1);
      return;
    }
    let cancelled = false;
    (async () => {
      const data = await listSiblingContas?.();
      if (cancelled || !data) return;
      setContas(data.contas || []);
      setCurrentIndex(typeof data.currentIndex === "number" ? data.currentIndex : -1);
    })();
    return () => { cancelled = true; };
  }, [isCompanyMode, user?.companyId, listSiblingContas]);

  const switchToConta = useCallback(async (conta) => {
    if (!conta || switching) return;
    if (conta.id === user?.companyId) {
      setDropdownOpen(false);
      return;
    }
    setSwitching(true);
    setDropdownOpen(false);
    await superSwitchCompany(conta.id);
    // applyTokenAndReload → window.location.assign, então não precisa
    // resetar setSwitching aqui.
  }, [switching, user?.companyId, superSwitchCompany]);

  const handlePrev = useCallback(() => {
    if (currentIndex <= 0 || !contas.length) return;
    switchToConta(contas[currentIndex - 1]);
  }, [currentIndex, contas, switchToConta]);

  const handleNext = useCallback(() => {
    if (currentIndex < 0 || currentIndex >= contas.length - 1) return;
    switchToConta(contas[currentIndex + 1]);
  }, [currentIndex, contas, switchToConta]);

  const handleReturn = async () => {
    if (busy) return;
    setBusy(true);
    await returnFromImpersonation();
  };

  if (!impersonation) return null;

  const Icon = isUserMode ? UserRoundCheck : Building2;
  const returnLabel = isUserMode ? "Voltar para minha conta" : "Sair da empresa";
  const empresaName = user?.company?.groupName || "—";
  const contaName = user?.company?.name || "—";
  const hasMultipleContas = isCompanyMode && contas.length > 1;
  const canPrev = hasMultipleContas && currentIndex > 0 && !switching;
  const canNext = hasMultipleContas && currentIndex >= 0 && currentIndex < contas.length - 1 && !switching;

  return (
    <div className={classes.bar} role="status" aria-live="polite">
      <span className={classes.iconChip}>
        <Icon size={15} strokeWidth={2} />
      </span>

      <span className={classes.message}>
        {isUserMode ? (
          <>
            <span className={classes.line}>
              Acessando como{" "}
              <span className={classes.strong}>{user?.name || "usuário"}</span>
              {impersonation.originalUserName && (
                <>
                  {" "}(por <span className={classes.strong}>{impersonation.originalUserName}</span>)
                </>
              )}
            </span>
          </>
        ) : (
          <>
            <span className={classes.line}>
              Empresa ativa: <span className={classes.strong}>{empresaName}</span>
            </span>
            <span className={classes.line}>
              Conta ativa: <span className={classes.strong}>{contaName}</span>
            </span>
          </>
        )}
      </span>

      {hasMultipleContas && (
        <div className={classes.contaNav} ref={dropdownRef}>
          <button
            type="button"
            className={classes.navBtn}
            onClick={handlePrev}
            disabled={!canPrev}
            aria-label="Conta anterior"
            title="Conta anterior"
          >
            <ChevronLeft size={15} strokeWidth={2.25} />
          </button>

          <div className={classes.dropdownWrap}>
            <button
              type="button"
              className={classes.navName}
              onClick={() => setDropdownOpen(v => !v)}
              disabled={switching}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              title="Trocar conta"
            >
              <span className={classes.navNameText}>{contaName}</span>
              <ChevronDown size={13} strokeWidth={2} />
            </button>
            {dropdownOpen && (
              <div className={classes.dropdownMenu} role="listbox">
                {contas.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`${classes.dropdownItem} ${i === currentIndex ? classes.dropdownItemActive : ""}`}
                    onClick={() => switchToConta(c)}
                    role="option"
                    aria-selected={i === currentIndex}
                  >
                    <Building2 size={14} strokeWidth={1.75} />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className={classes.navBtn}
            onClick={handleNext}
            disabled={!canNext}
            aria-label="Próxima conta"
            title="Próxima conta"
          >
            <ChevronRight size={15} strokeWidth={2.25} />
          </button>

          <span className={classes.counter}>
            {currentIndex >= 0 ? `${currentIndex + 1}/${contas.length}` : `—/${contas.length}`}
          </span>
        </div>
      )}

      <button
        type="button"
        className={classes.returnBtn}
        onClick={handleReturn}
        disabled={busy}
        aria-label={returnLabel}
      >
        <LogOut size={13} strokeWidth={2} />
        {busy ? "Voltando..." : returnLabel}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
